import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';
import { useSoundStatus } from '../../context/SoundStatusContext';
import { useTour } from '../../context/TourContext';
import { parseDescription, shuffleArray } from '../../utils';

import Intro from '../UI/game/intro';
import Header from '../UI/game/header';
import Modal from '../UI/modal';
import FooterMessage from '../UI/game/footer-message';
import Hint from '../UI/hint';

import {
  generalInfoQuery,
  updateGameSession,
  postDecisionsBulk,
  getDecisions,
} from './api';

import {
  calculateScore,
  saveSessionToStorage,
  loadSessionFromStorage,
  clearSessionFromStorage,
  formatDecisionsForBulkPost,
} from './validation';

import { getRealcoCompany } from './realcoData';

import './styles.css';
import './multistep.css';

import CountdownSound from './sounds/countdown.mp3';
import GameProcessSound from './sounds/game-process.mp3';
import ClickSound from './sounds/click.mp3';
import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';

// Section drop zones. `matches` is the list of candidate option names
// we accept when looking up the section's account_item id from the
// scenario level's options — covers both `"Current Assets"` and
// `"Current Assets Section"` shapes, case-insensitively.
const BALANCE_SHEET_SECTIONS = [
  { key: 'current-assets',          label: 'Current Assets',
    matches: ['Current Assets', 'Current Assets Section'] },
  { key: 'non-current-assets',      label: 'Non-Current Assets',
    matches: ['Non-Current Assets', 'Non-Current Assets Section', 'Long-term Assets', 'Long-term Assets Section'] },
  { key: 'current-liabilities',     label: 'Current Liabilities',
    matches: ['Current Liabilities', 'Current Liabilities Section'] },
  { key: 'non-current-liabilities', label: 'Non-Current Liabilities',
    matches: ['Non-Current Liabilities', 'Non-Current Liabilities Section', 'Long-term Liabilities', 'Long-term Liabilities Section'] },
  { key: 'equity',                  label: 'Equity',
    matches: ['Equity', 'Equity Section'] },
];

const INCOME_STATEMENT_SECTIONS = [
  { key: 'gross-profit',     label: 'Gross Profit',
    matches: ['Gross Profit', 'Gross Profit Section', 'Gross Profits', 'Gross Profits Section'] },
  { key: 'operating-profit', label: 'Operating Profit',
    matches: ['Operating Profit', 'Operating Profit Section', 'Operating Expenses', 'Operating Expenses Section'] },
  { key: 'non-operating',    label: 'Non-Operating',
    matches: ['Non-Operating', 'Non-Operating Section', 'Other Income/Expenses', 'Other Income or Expenses'],
    subLabel: '(Other Income/Expenses)' },
];

// Realco-only: the Net Income section becomes a drop zone that accepts
// the Income Tax Expense item (validated via its parent "Net Profit
// Section"). On drop, the item is rendered as a "Less: …" row in the
// drop zone rather than a regular brick.
const NET_INCOME_REALCO_SECTION = {
  key: 'net-income',
  label: 'Net Income',
  matches: ['Net Profit Section', 'Net Profit', 'Net Income Section', 'Net Income'],
};

const normalize = (s) => (s || '').toString().trim().toLowerCase();

const formatMoney = (n) => {
  if (n == null || Number.isNaN(n)) return '$0';
  const abs = Math.abs(n);
  return `${n < 0 ? '-' : ''}$${abs.toLocaleString('en-US')}`;
};

// Extract the dollar amount for a question. The instructor-configured
// dollar value lives in the question's `explanation` field — strip any
// formatting ($, commas, parentheses for negatives) and parse to a
// number. Fall back to amount/value fields, then an order-based
// placeholder, so a missing explanation never blanks the board.
const parseExplanationAmount = (raw) => {
  if (raw == null) return null;
  const str = String(raw).trim();
  if (!str) return null;
  // Accounting-style negatives in parentheses, e.g. "(1,000,000)".
  const isNegative = /^\(.*\)$/.test(str) || /^-/.test(str);
  const digits = str.replace(/[^0-9.]/g, '');
  if (!digits) return null;
  const n = parseFloat(digits);
  if (Number.isNaN(n)) return null;
  return isNegative ? -n : n;
};

const getAmount = (q) => {
  const fromExplanation = parseExplanationAmount(q?.explanation);
  if (fromExplanation != null) return fromExplanation;
  return Number(q?.amount)
    || Number(q?.value)
    || Number(q?.account_item?.amount)
    || Number(q?.account_item?.value)
    || (q?.order ? q.order * 1_000_000 : 1_000_000);
};

export default function ClassificationMultiStep({ levelNumber, scenarioLevels, answerData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { startTour } = useTour();

  const countdownEffect = useRef(new Audio(CountdownSound));
  const gameProcessEffect = useRef(new Audio(GameProcessSound));
  const clickEffect = useRef(new Audio(ClickSound));
  const answerCorrectEffect = useRef(new Audio(AnswerCorrectSound));
  const answerWrongEffect = useRef(new Audio(AnswerWrongSound));

  // Stop the looping game-process sound if we leave the page.
  useEffect(() => {
    const currentGameProcessEffect = gameProcessEffect.current;
    return () => { if (currentGameProcessEffect) currentGameProcessEffect.pause(); };
  }, [location]);

  const { getUser, getCurrentClassificationGameId } = useAuth();
  const user = getUser();
  const gameId = getCurrentClassificationGameId();

  const { soundStatus } = useSoundStatus();

  const { data: generalInfo } = useQuery(generalInfoQuery());
  const gamesSessions = generalInfo?.sessions;
  const games = generalInfo?.games;

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  useEffect(() => {
    if (!gamesSessions || !gameId || !user) return;
    const inProgress = gamesSessions.find(s => s.user === user.id && s.game === gameId && !s.completed);
    if (inProgress) { setCurrentGameSessionId(inProgress.id); return; }
    const mostRecent = gamesSessions
      .filter(s => s.user === user.id && s.game === gameId && s.completed)
      .reduce((max, s) => (s.id > (max?.id || 0) ? s : max), null);
    if (mostRecent) setCurrentGameSessionId(mostRecent.id);
  }, [gamesSessions, gameId, user]);

  // Fetch the server's record of decisions for this game/session/level so
  // the level can rehydrate its placed-bricks state on a refresh even
  // after localStorage has been cleared by the end-of-level bulk POST.
  // Mirrors index.js — without this, the multistep level reloaded blank.
  const { data: decisionsResponse, isSuccess: isDecisionsSuccess } = useQuery({
    queryKey: ['decisionsResponse', { levelNumber, userId: user?.id, gameId, gameSessionId: currentGameSessionId }],
    queryFn: () => getDecisions({ levelNumber, userId: user?.id, gameId, gameSessionId: currentGameSessionId }),
    enabled: !!user?.id && levelNumber !== undefined && !!gameId && !!currentGameSessionId,
    refetchOnWindowFocus: false,
  });

  // Bounce to tutorial if there's no ready session — same guard the other
  // classification variants enforce.
  useEffect(() => {
    if (!gameId || !gamesSessions) return;
    const hasReady = gamesSessions.some(
      s => s.user === user?.id && s.is_ready === true && s.game === gameId
    );
    if (!hasReady) navigate('/classification/level/1/tutorial');
  }, [gamesSessions, gameId, user?.id, navigate]);

  // Replay guard: if this level is already completed in the resolved
  // session (e.g. the player hit the back button after completing it),
  // skip past the gameplay component and land them on the points page —
  // which itself handles tournament-mode redirects to the next level or
  // summary. Fires once per mount via a ref so it can't re-trigger
  // after the player completes this level mid-session.
  const replayGuardCheckedRef = useRef(false);
  useEffect(() => {
    if (replayGuardCheckedRef.current) return;
    if (!gamesSessions || !currentGameSessionId) return;
    const session = gamesSessions.find(s => s.id === currentGameSessionId);
    if (!session) return;
    replayGuardCheckedRef.current = true;
    if (session[`level${levelNumber}`] === 'completed') {
      navigate(`/classification/level/${levelNumber}/points/`, { replace: true });
    }
  }, [gamesSessions, currentGameSessionId, levelNumber, navigate]);

  const scenarioLevel = useMemo(
    () => scenarioLevels?.find(l => l.level === levelNumber) || null,
    [scenarioLevels, levelNumber]
  );

  // The Classified Multi-Step level (name === 'classified-multistep') runs
  // a level-end scoring flow: each decision is sent with score_points: null
  // and the backend computes the level score from the scenario level's
  // `scoring` config. The header's points pill is hidden too — the final
  // score arrives via a separate fetch (handled in a follow-up). All other
  // variants routed through this component (e.g. design=['4'] with a
  // different name) keep the standard per-decision scoring + visible pill.
  // Scoring mode comes from the scenario level's `scoring` field
  // (backend-defined, 'level' or 'decision'). 'level' means the
  // backend computes one score for the whole level — the frontend
  // sends score_points: null per decision and hides the running
  // header pill since no aggregate exists during play.
  const isLevelScoring = scenarioLevel?.scoring === 'level';

  // Name check is kept ONLY to drive the per-level reflection-tour
  // selection (the tour is named after the level, not the scoring
  // mode). Realco has its own tour wired up separately.
  const isClassifiedMultistep = scenarioLevel?.name === 'classified-multistep';

  // Design 5 (Real Companies). Same gameplay as classified-multistep
  // but with a company-branded header above the brick stack and the
  // stack capped to 15 items per batch — the next batch appears only
  // after the current one is fully placed correctly. The new level
  // name follows the `real-public-co-<DisplayName>` convention.
  const isRealco = (scenarioLevel?.design?.includes?.('5'))
    || (scenarioLevel?.name?.startsWith?.('real-public-co') ?? false);
  // Memoize the resolved company so the reference is stable across
  // renders — without this, every render creates a new object that
  // invalidates downstream effect deps (e.g. the modal/countdown
  // effect), causing the countdown interval to restart on every tick.
  const realcoCompany = useMemo(
    () => (isRealco ? getRealcoCompany(scenarioLevel?.name) : null),
    [isRealco, scenarioLevel?.name]
  );
  const REALCO_BATCH_SIZE = 15;

  // Sales Revenue (in billions) used by the realco reflection tour's
  // Step 3 copy. Pull the raw value from the question whose account
  // item is "Sales Revenue", divide by 1000, and format to two
  // decimals (e.g. raw 416161 → "416.16"). Falls back to "0.00" so
  // the tour copy never reads "$ billion".
  const realcoSalesRevenueBillions = useMemo(() => {
    if (!isRealco) return null;
    const sr = (scenarioLevel?.questions || []).find(q =>
      /sales\s*revenue/i.test(q?.account_item?.name || '')
    );
    const raw = parseExplanationAmount(sr?.explanation);
    if (raw == null) return '0.00';
    return (Math.abs(raw) / 1000).toFixed(2);
  }, [isRealco, scenarioLevel]);

  // The scenario-level response already embeds `questions` and `options`
  // with their `account_item` data, and `answerData` (items-view) carries
  // each item's `parent_ids`. That's everything we need — no separate
  // calls to `/items/`, `/item-options/`, or `/item-questions/`.

  // Match each section to a scenario-level option by trying the list of
  // candidate names (case-insensitive), so the lookup succeeds whether
  // the backend stores `"Current Assets"` or `"Current Assets Section"`.
  //
  // We need two maps keyed off the same section names because the section
  // serves two different roles:
  //   - account_item.id  → drives the gameplay logic (matches each
  //     question's parent_ids back to a section, since parent_ids on the
  //     items-view are account_item ids).
  //   - option.id (PK)   → what the bulk-decisions endpoint expects as
  //     `selected_option`. Sending account_item.id here is what produced
  //     "Invalid pk - object does not exist" errors for sections whose
  //     account_item.id didn't happen to coincide with a valid option PK.
  const sectionAccountItemIdByKey = useMemo(() => {
    const map = {};
    // Realco lists net-income BEFORE the other income-statement
    // sections so an item with multiple section parents (e.g. Income
    // Tax Expense flagged under both "Net Profit Section" and
    // "Non-Operating") resolves to net-income first.
    const all = isRealco
      ? [...BALANCE_SHEET_SECTIONS, NET_INCOME_REALCO_SECTION, ...INCOME_STATEMENT_SECTIONS]
      : [...BALANCE_SHEET_SECTIONS, ...INCOME_STATEMENT_SECTIONS];
    const optionsByName = new Map();
    (scenarioLevel?.options || []).forEach(o => {
      const ai = o?.account_item;
      if (ai?.name && ai.id != null) optionsByName.set(normalize(ai.name), ai.id);
    });
    all.forEach(section => {
      for (const candidate of section.matches) {
        const id = optionsByName.get(normalize(candidate));
        if (id != null) { map[section.key] = id; break; }
      }
    });
    if (scenarioLevel?.options?.length && Object.keys(map).length === 0) {
      // Surface a one-liner so we can see what names the backend returned
      // when nothing matches — keeps future debugging fast.
      // eslint-disable-next-line no-console
      console.warn(
        '[multistep] no sections matched. scenario-level option names:',
        (scenarioLevel.options || []).map(o => o?.account_item?.name)
      );
    }
    return map;
  }, [scenarioLevel, isRealco]);

  const sectionOptionIdByKey = useMemo(() => {
    const map = {};
    // Realco lists net-income BEFORE the other income-statement
    // sections so an item with multiple section parents (e.g. Income
    // Tax Expense flagged under both "Net Profit Section" and
    // "Non-Operating") resolves to net-income first.
    const all = isRealco
      ? [...BALANCE_SHEET_SECTIONS, NET_INCOME_REALCO_SECTION, ...INCOME_STATEMENT_SECTIONS]
      : [...BALANCE_SHEET_SECTIONS, ...INCOME_STATEMENT_SECTIONS];
    const optionsByName = new Map();
    (scenarioLevel?.options || []).forEach(o => {
      const name = o?.account_item?.name;
      if (name && o?.id != null) optionsByName.set(normalize(name), o.id);
    });
    all.forEach(section => {
      for (const candidate of section.matches) {
        const id = optionsByName.get(normalize(candidate));
        if (id != null) { map[section.key] = id; break; }
      }
    });
    return map;
  }, [scenarioLevel, isRealco]);

  // The Revenues parent's account_item id — used to split items inside
  // the Gross Profit drop zone into the "Net Revenues" subtotal group
  // (Sales Revenue, Sales Discount, …) versus the rest (COGS, …).
  const revenuesOptionId = useMemo(() => {
    const opt = (scenarioLevel?.options || []).find(
      o => normalize(o?.account_item?.name) === 'revenues'
    );
    return opt?.account_item?.id ?? null;
  }, [scenarioLevel]);

  // Resolve each question's parent ID list from answerData. Names aren't
  // needed because matching to sections happens purely by id.
  const questions = useMemo(() => {
    const rawQuestions = scenarioLevel?.questions || [];
    if (!rawQuestions.length) return [];
    return [...rawQuestions]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(q => {
        const account = q.account_item || {};
        const accountItemId = account.id ?? q.account_item;
        const answerEntry = answerData?.find(a => a.item_id === accountItemId);
        const parentIds = answerEntry?.parent_ids || [];
        return {
          id: q.id,
          order: q.order,
          accountItemId,
          name: account.name,
          // Hover description for realco (mirrors the class-default
          // tooltip). Backend may store it on the account_item or on
          // the question's explanation field — fall back to both.
          description: account.description || q.description || q.explanation || '',
          amount: getAmount({ ...q, account_item: account }),
          parentIds,
        };
      });
  }, [scenarioLevel, answerData]);

  const sectionKeyForItem = useCallback((item) => {
    // Match against account_item ids — `parentIds` on each question come
    // from the items-view and are account_item ids, not option PKs.
    const entries = Object.entries(sectionAccountItemIdByKey);
    const match = entries.find(([, accountItemId]) => item.parentIds?.includes(accountItemId));
    return match ? match[0] : null;
  }, [sectionAccountItemIdByKey]);

  // Game state ---------------------------------------------------------
  const [stack, setStack] = useState([]);
  // Realco only: items not yet displayed. The stack shows the active
  // batch of 15; once it drains, we move the next 15 from here into
  // the stack and re-run the fall animation.
  const [pendingQueue, setPendingQueue] = useState([]);
  const [placed, setPlaced] = useState({});
  const [spawnIndex, setSpawnIndex] = useState(0);
  const [isStackReady, setIsStackReady] = useState(false);
  const [dragId, setDragId] = useState(null);
  // Tracks which brick is hovered so the description Hint can be
  // rendered inside it (mirrors class-default's hover tooltip).
  // Applies to both classified-multistep and realco — any brick with
  // a description gets the tooltip.
  const [hoveredBrickId, setHoveredBrickId] = useState(null);
  const [errorFlashSection, setErrorFlashSection] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [shouldScorePlaySound, setShouldScorePlaySound] = useState(false);
  const [time, setTime] = useState(0);

  // Decisions accumulate locally during gameplay (also mirrored to
  // localStorage so a refresh doesn't lose them) and are sent in a
  // single bulk POST at the end of the level — same pattern as the
  // standard Classification game in index.js.
  const [allDecisions, setAllDecisions] = useState([]);
  const [questionTries, setQuestionTries] = useState({});
  const gameStartRef = useRef(null);
  // Anchor for per-decision `time_elapsed`. Each drop stores delta from
  // this point, then advances it to the drop time so the next decision
  // measures only against itself. Falls back to gameStartRef on the very
  // first drop.
  const lastDecisionMsRef = useRef(null);
  const postInFlightRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [modalToShow, setModalToShow] = useState(null);
  const [modalContent, setModalContent] = useState(
    <div className="modal-center vertical">
      <div><span>Game start!</span></div>
      <div><span className="modal-counter">3</span></div>
    </div>
  );
  const [isModalOverlayVisible, setIsModalOverlayVisible] = useState(true);
  const [isModalSmall, setIsModalSmall] = useState(false);

  const [isFooterMessageShown, setIsFooterMessageShown] = useState(false);
  const [footerMessageType, setFooterMessageType] = useState();
  const [footerMessageDescription, setFooterMessageDescription] = useState();

  // Intro panel values — mirrored from scramble.js (tournament-aware).
  const [isGameBelongsToTournament, setIsGameBelongsToTournament] = useState(false);
  const [leftIntroTitle, setLeftIntroTitle] = useState(null);
  const [leftIntroSubtitle, setLeftIntroSubtitle] = useState(null);
  const [leftIntroDescription, setLeftIntroDescription] = useState(null);
  const [rightIntroTitle, setRightIntroTitle] = useState(null);
  const [rightIntroSubtitle, setRightIntroSubtitle] = useState(null);
  const [rightIntroDescription, setRightIntroDescription] = useState(null);
  const [isIntroContentCentered, setIsIntroContentCentered] = useState(false);
  const [isIntroSubtitleContentReversed, setIsIntroSubtitleContentReversed] = useState(false);

  useEffect(() => {
    if (!gameId || !scenarioLevel || !games) return;
    const currentGame = games.find(g => g.id === gameId);
    const belongs = currentGame?.tournament_ids?.length > 0;
    setIsGameBelongsToTournament(belongs);
    if (belongs) {
      setLeftIntroTitle('Team 1');
      setLeftIntroSubtitle('0 pts');
      setLeftIntroDescription('(Your Team)');
      setRightIntroTitle('Team 2');
      setRightIntroSubtitle('0 pts');
      setRightIntroDescription('(Your Team)');
      setIsIntroContentCentered(true);
      setIsIntroSubtitleContentReversed(true);
    } else {
      setLeftIntroTitle(`Level ${levelNumber}`);
      const { subtitle, description } = parseDescription(scenarioLevel.description);
      setLeftIntroSubtitle(subtitle);
      setRightIntroDescription(description);
    }
  }, [games, gameId, levelNumber, scenarioLevel]);

  const showFooter = (type, description) => {
    setFooterMessageType(type);
    setFooterMessageDescription(description);
    setIsFooterMessageShown(true);
  };

  const playSound = (ref) => {
    if (soundStatus !== 'enabled') return;
    try { ref.current.currentTime = 0; ref.current.play().catch(() => {}); } catch { /* ignore */ }
  };

  // Game process background loop, matching scramble.js behaviour.
  useEffect(() => {
    if (isPlaying && soundStatus === 'enabled') {
      gameProcessEffect.current.volume = 0.09;
      gameProcessEffect.current.loop = true;
      gameProcessEffect.current.play().catch(() => {});
    } else {
      gameProcessEffect.current.pause();
    }
    const ref = gameProcessEffect.current;
    return () => { ref.pause(); };
  }, [isPlaying, soundStatus]);

  useEffect(() => {
    const onClick = () => { if (soundStatus === 'enabled') clickEffect.current.play().catch(() => {}); };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [soundStatus]);

  // Seed the stack once questions resolve. Every question falls — even
  // ones whose parents don't currently map to a drop zone — and the
  // display order is randomized so two students never see the same fall
  // sequence. The original `order` field is preserved on each item so we
  // can sort placed items by it inside their drop zones.
  //
  // Also rehydrates any local progress for this exact game/level: items
  // previously placed correctly are restored to their drop zones, and
  // `allDecisions` / `questionTries` / `totalScore` are restored so the
  // bulk POST at level-end carries the full history (mirrors the same
  // recovery the standard Classification game does).
  useEffect(() => {
    if (!questions.length || stack.length || Object.keys(placed).length) return;
    // Wait for the server-side decisions fetch so the merge below sees
    // both the API record and any local pending decisions. Without this
    // gate, a refresh after end-of-level (which clears localStorage)
    // would rehydrate a blank board.
    if (!isDecisionsSuccess) return;

    const saved = currentGameSessionId ? loadSessionFromStorage(currentGameSessionId) : null;
    const isLocalForThisLevel =
      saved?.gameId === gameId && saved?.levelNumber === levelNumber;
    const localDecisions = isLocalForThisLevel ? (saved.allDecisions || []) : [];
    const localTries = isLocalForThisLevel ? (saved.questionTries || {}) : {};
    const localTotalScore = isLocalForThisLevel ? (saved.totalScore || 0) : 0;

    // Merge server decisions with any local pending. The server is the
    // canonical source after a successful bulk POST; local catches the
    // window between drop and POST when the user could refresh mid-level.
    const apiDecisions = decisionsResponse || [];
    const apiCorrectDecisions = apiDecisions.filter(d => d.is_correct === true);
    const localCorrectDecisions = localDecisions.filter(d => d.is_correct === true);
    const correctDecisions = [...apiCorrectDecisions, ...localCorrectDecisions];
    const placedByQuestionId = new Map();
    correctDecisions.forEach(d => placedByQuestionId.set(d.question, d.selected_option));

    if (placedByQuestionId.size) {
      const restoredPlaced = {};
      const remainingQuestions = [];
      questions.forEach(q => {
        if (placedByQuestionId.has(q.id)) {
          // Map the saved option-id back to a section key so the brick
          // shows up in the right column on rehydrate.
          const optionId = placedByQuestionId.get(q.id);
          const sectionKey = Object.entries(sectionOptionIdByKey)
            .find(([, id]) => id === optionId)?.[0]
            || sectionKeyForItem(q);
          if (sectionKey) {
            restoredPlaced[sectionKey] = [...(restoredPlaced[sectionKey] || []), q];
          } else {
            remainingQuestions.push(q);
          }
        } else {
          remainingQuestions.push(q);
        }
      });
      setPlaced(restoredPlaced);
      const shuffledRemaining = shuffleArray(remainingQuestions);
      if (isRealco) {
        setStack(shuffledRemaining.slice(0, REALCO_BATCH_SIZE));
        setPendingQueue(shuffledRemaining.slice(REALCO_BATCH_SIZE));
      } else {
        setStack(shuffledRemaining);
      }
      // If everything was already placed correctly (e.g. refresh after
      // completion when the replay guard hasn't picked up the session
      // update yet), flip isPlaying on so the end-of-game effect re-
      // surfaces the well-done modal — mirrors class-elements' setIsPlaying
      // /setIsLevelCompleted on full rehydrate.
      if (remainingQuestions.length === 0) {
        setIsPlaying(true);
      }
    } else {
      const shuffledAll = shuffleArray([...questions]);
      if (isRealco) {
        setStack(shuffledAll.slice(0, REALCO_BATCH_SIZE));
        setPendingQueue(shuffledAll.slice(REALCO_BATCH_SIZE));
      } else {
        setStack(shuffledAll);
      }
    }

    if (localDecisions.length) setAllDecisions(localDecisions);
    if (Object.keys(localTries).length) setQuestionTries(localTries);
    if (localTotalScore) setTotalScore(localTotalScore);
  }, [questions, stack.length, placed, currentGameSessionId, gameId, levelNumber, sectionOptionIdByKey, sectionKeyForItem, decisionsResponse, isDecisionsSuccess]);

  // Fall animation — spawn items into the stack one at a time.
  useEffect(() => {
    if (!stack.length) return;
    if (spawnIndex >= stack.length) {
      if (!isStackReady) setIsStackReady(true);
      return;
    }
    const t = setTimeout(() => setSpawnIndex(v => v + 1), 120);
    return () => clearTimeout(t);
  }, [spawnIndex, stack.length, isStackReady]);

  // Realco only: when the current batch of 15 fully drains (all placed
  // correctly) and there are more items waiting, load the next batch
  // and re-run the fall animation. Gated on isPlaying so we don't fire
  // during the initial empty render before the seed effect runs.
  useEffect(() => {
    if (!isRealco || !isPlaying) return;
    if (stack.length > 0 || pendingQueue.length === 0) return;
    const nextBatch = pendingQueue.slice(0, REALCO_BATCH_SIZE);
    const remainder = pendingQueue.slice(REALCO_BATCH_SIZE);
    setStack(nextBatch);
    setPendingQueue(remainder);
    setSpawnIndex(0);
    setIsStackReady(false);
  }, [isRealco, isPlaying, stack.length, pendingQueue]);

  // Countdown modal once the intro closes and the stack is ready.
  // Also wait for currentGameSessionId — drops that happen before the
  // session id resolves are written to React state but skipped for
  // localStorage (the save call is gated on a truthy session id), and
  // the end-of-level POST prefers localStorage. Holding the countdown
  // until the id arrives keeps state and localStorage consistent so
  // every decision the player makes ends up in the bulk post.
  useEffect(() => {
    if (showIntro || !isStackReady || isPlaying || modalToShow) return;
    if (!currentGameSessionId) return;
    // Single-shot: once the countdown has run and the game timer started,
    // never re-trigger. After the level ends, isPlaying flips back to
    // false and View Summary nulls modalToShow before the reflection
    // tour fires — without this guard, those two states would re-satisfy
    // the conditions above and a phantom countdown would play behind
    // the tour overlay.
    if (gameStartRef.current != null) return;
    setModalToShow('countdown');
    // Play the countdown sound on a 1s delay to match the first
    // setInterval tick in runCountdown — same pattern as the
    // class-elements flow in index.js. The delay also gives the
    // browser time to decode the clip on first play.
    if (soundStatus === 'enabled') {
      setTimeout(() => {
        try { countdownEffect.current.currentTime = 0; } catch { /* ignore */ }
        countdownEffect.current.play().catch(() => {});
      }, 1000);
    }
  }, [showIntro, isStackReady, isPlaying, modalToShow, currentGameSessionId, soundStatus]);

  const runCountdown = useCallback(() => {
    let countdown = 3;
    const interval = setInterval(() => {
      if (countdown === 0) {
        clearInterval(interval);
        setModalToShow(null);
        setIsPlaying(true);
        // Anchor the game-start time so each decision can record its
        // own time-elapsed for scoring + the bulk post at level end.
        if (gameStartRef.current == null) gameStartRef.current = Date.now();
      } else {
        setModalContent(
          <div className="modal-center vertical">
            <div><span>Game start!</span></div>
            <div><span>{countdown}</span></div>
          </div>
        );
        countdown--;
      }
    }, 1000);
    return () => {
      clearInterval(interval);
      try { countdownEffect.current.pause(); } catch { /* ignore */ }
    };
  }, []);

  // Wait for the in-flight bulk post (if any) and retry once if local-
  // storage still holds decisions — i.e. the completion POST didn't
  // succeed. Mirrors index.js's `ensureDecisionsPosted`. Declared here
  // (above the modal effect that uses it) so the dependency reference
  // doesn't fall into the temporal dead zone.
  const ensureDecisionsPosted = useCallback(async () => {
    if (postInFlightRef.current) {
      try { await postInFlightRef.current; } catch { /* logged at the source */ }
    }
    if (!currentGameSessionId) return;
    const savedSession = loadSessionFromStorage(currentGameSessionId);
    if (savedSession?.allDecisions?.length) {
      try {
        const formatted = formatDecisionsForBulkPost(savedSession.allDecisions, time);
        await postDecisionsBulk(formatted);
        clearSessionFromStorage(currentGameSessionId);
      } catch (err) {
        console.error('Retry post multistep decisions failed:', err);
      }
    }
  }, [currentGameSessionId, time]);

  useEffect(() => {
    if (modalToShow === 'countdown' && !showIntro) {
      setIsModalOverlayVisible(true);
      setIsModalSmall(false);
      return runCountdown();
    }
    if (modalToShow === 'well-done') {
      setIsModalSmall(true);
      setIsModalOverlayVisible(false);
      const handleViewSummaryClick = async () => {
        await ensureDecisionsPosted();
        const navigateToSummary = () => navigate(`/classification/level/${levelNumber}/points/`);
        // Per-level reflection tour. Skip for the in-tournament
        // intermediate-level path (button reads "Next Level"), where the
        // student is mid-tournament and shouldn't be interrupted.
        const isViewSummaryButton = !(isGameBelongsToTournament && scenarioLevels.length !== levelNumber);
        if (isClassifiedMultistep && isViewSummaryButton) {
          setModalToShow(null);
          startTour('classified-multistep-reflection', { onComplete: navigateToSummary });
          return;
        }
        if (isRealco && isViewSummaryButton) {
          setModalToShow(null);
          startTour('realco-reflection', {
            onComplete: navigateToSummary,
            data: {
              companyName: realcoCompany?.displayName || '',
              salesRevenueBillions: realcoSalesRevenueBillions || '0.00',
            },
          });
          return;
        }
        navigateToSummary();
      };
      const handleCloseTabClick = async () => {
        await ensureDecisionsPosted();
        window.close();
      };
      setModalContent(
        <div className="modal-center vertical">
          <div><span>Well done!</span></div>
          {isGameBelongsToTournament && scenarioLevels.length === levelNumber
            ? <button onClick={handleCloseTabClick} className="button-blue button-view-summary">Close</button>
            : <button
                onClick={handleViewSummaryClick}
                className="button-blue button-view-summary"
              >
                {isGameBelongsToTournament && scenarioLevels.length !== levelNumber ? 'Next Level' : 'View Summary'}
              </button>
          }
        </div>
      );
    }
    return undefined;
  }, [modalToShow, showIntro, runCountdown, levelNumber, scenarioLevels, isGameBelongsToTournament, navigate, ensureDecisionsPosted, isClassifiedMultistep, isLevelScoring, isRealco, realcoCompany, realcoSalesRevenueBillions, startTour]);

  // Time ticker
  useEffect(() => {
    if (!isPlaying) return undefined;
    const id = setInterval(() => setTime(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [isPlaying]);

  const updateSessionMutation = useMutation({ mutationFn: (data) => updateGameSession(data) });

  const visibleStack = stack.slice(0, spawnIndex);

  const handleDragStart = (e, item) => {
    if (!isPlaying) { e.preventDefault(); return; }
    setDragId(item.id);
    try { e.dataTransfer.setData('text/plain', String(item.id)); } catch { /* ignore */ }
    e.dataTransfer.effectAllowed = 'move';
    // Explicitly pin the drag preview to just this brick so the browser
    // doesn't snapshot adjacent stacked bricks (whose hard offset shadows
    // can overlap into the dragged element's bounding box).
    if (e.currentTarget && typeof e.dataTransfer.setDragImage === 'function') {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      e.dataTransfer.setDragImage(e.currentTarget, offsetX, offsetY);
    }
  };
  const handleDragEnd = () => setDragId(null);

  const handleSectionDragOver = (e) => {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e, sectionKey) => {
    e.preventDefault();
    if (!dragId) return;
    const item = stack.find(s => s.id === dragId);
    if (!item) { setDragId(null); return; }

    const correctSectionKey = sectionKeyForItem(item);
    const isCorrect = correctSectionKey === sectionKey;
    const sectionOptionId = sectionOptionIdByKey[sectionKey] ?? null;

    // Tries-per-question + score: same logic as the standard
    // Classification game (validation.calculateScore).
    const currentTries = (questionTries[item.id] || 0) + 1;
    const nextQuestionTries = { ...questionTries, [item.id]: currentTries };
    setQuestionTries(nextQuestionTries);

    // Per-decision timing:
    //   time_elapsed       = seconds since the previous decision (delta);
    //                        falls back to game-start for the very first
    //                        drop so the first row isn't a 0.
    //   game_time_elapsed  = seconds since game-start (cumulative); grows
    //                        monotonically across decisions.
    const nowMs = Date.now();
    const gameStartMs = gameStartRef.current || nowMs;
    const lastDecisionMs = lastDecisionMsRef.current || gameStartMs;
    const timeElapsedSeconds = Math.max(1, Math.round((nowMs - lastDecisionMs) / 1000));
    const gameTimeElapsedSeconds = Math.max(1, Math.round((nowMs - gameStartMs) / 1000));
    lastDecisionMsRef.current = nowMs;
    // For classified-multistep, score_points stays null per decision —
    // backend computes the level score from the scenario level's `scoring`
    // config. All other variants keep the standard per-decision calc.
    const scorePoints = isLevelScoring
      ? null
      : (isCorrect ? calculateScore({ timeElapsedSeconds, totalTries: currentTries }) : 0);

    // Buffer the decision locally + persist to localStorage so a refresh
    // mid-level doesn't lose them. They're posted as a single bulk POST
    // when the level finishes.
    const newDecision = {
      game: gameId,
      game_session: currentGameSessionId,
      question: item.id,
      selected_option: sectionOptionId,
      time_elapsed: timeElapsedSeconds,
      game_time_elapsed: gameTimeElapsedSeconds,
      timestamp: new Date().toISOString(),
      is_correct: isCorrect,
      score_points: scorePoints,
      score_accuracy: isCorrect ? 1 : 0,
      total_tries: currentTries,
    };
    const nextDecisions = [...allDecisions, newDecision];
    setAllDecisions(nextDecisions);
    if (currentGameSessionId) {
      saveSessionToStorage(currentGameSessionId, {
        gameId,
        levelNumber,
        questionTries: nextQuestionTries,
        allDecisions: nextDecisions,
        totalScore: !isLevelScoring && isCorrect ? totalScore + scorePoints : totalScore,
      });
    }

    if (isCorrect) {
      setStack(prev => prev.filter(s => s.id !== item.id));
      setPlaced(prev => ({
        ...prev,
        [sectionKey]: [...(prev[sectionKey] || []), item],
      }));
      if (!isLevelScoring) {
        setTotalScore(s => s + scorePoints);
        setShouldScorePlaySound(true);
      }
      playSound(answerCorrectEffect);
      showFooter('success', <p><b>{item.name}</b> belongs in <b>{sectionKey.replace(/-/g, ' ')}</b>.</p>);
    } else {
      setErrorFlashSection(sectionKey);
      setTimeout(() => setErrorFlashSection(curr => (curr === sectionKey ? null : curr)), 600);
      playSound(answerWrongEffect);
      showFooter('error', <p><b>{item.name}</b> does NOT belong in <b>{sectionKey.replace(/-/g, ' ')}</b>.</p>);
    }
    setDragId(null);
  };

  // End-of-game detection.
  useEffect(() => {
    if (!isPlaying || stack.length) return;
    // Realco runs in batches — the level isn't done until the pending
    // queue has also drained. Otherwise the well-done modal would pop
    // after only the first 15 items.
    if (isRealco && pendingQueue.length) return;
    if (!Object.keys(placed).length) return;
    setIsPlaying(false);
    setModalToShow('well-done');
    if (currentGameSessionId) {
      updateSessionMutation.mutate({
        id: currentGameSessionId,
        levelNumber,
        levelStatus: 'completed',
        isSessionCompleted: scenarioLevels?.length === levelNumber,
      });
    }

    // Bulk-post all the decisions accumulated this level. Same shape /
    // mutation as the standard Classification game in index.js — local-
    // storage holds the canonical copy in case the post fails so the
    // View Summary handler can retry.
    postInFlightRef.current = (async () => {
      try {
        const savedSession = currentGameSessionId
          ? loadSessionFromStorage(currentGameSessionId)
          : null;
        const decisionsToPost = savedSession?.allDecisions?.length
          ? savedSession.allDecisions
          : allDecisions;
        if (decisionsToPost.length) {
          const formatted = formatDecisionsForBulkPost(decisionsToPost, time);
          await postDecisionsBulk(formatted);
          if (currentGameSessionId) clearSessionFromStorage(currentGameSessionId);
        }
      } catch (err) {
        console.error('Failed to post multistep decisions:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, stack.length, placed, levelNumber, scenarioLevels?.length, currentGameSessionId, isRealco, pendingQueue.length]);

  // Totals & subtotals — sum by section drop zone (driven by id, not name)
  // and roll subtotals up into group totals so we don't depend on a stand-
  // alone "Assets"/"Liabilities" parent existing in the options array.
  const sumPlaced = useCallback((sectionKey) =>
    (placed[sectionKey] || []).reduce((s, it) => s + (it.amount || 0), 0),
  [placed]);

  const totalCurrentAssets = sumPlaced('current-assets');
  const totalNonCurrentAssets = sumPlaced('non-current-assets');
  const totalAssets = totalCurrentAssets + totalNonCurrentAssets;
  const totalCurrentLiabilities = sumPlaced('current-liabilities');
  const totalNonCurrentLiabilities = sumPlaced('non-current-liabilities');
  const totalLiabilities = totalCurrentLiabilities + totalNonCurrentLiabilities;
  const totalEquity = sumPlaced('equity');

  // Inside Gross Profit, "Net Revenues" sums every item whose parent is
  // "Revenues" — that nets Sales Revenue against contra-revenue items
  // like Sales Discount. "Total Gross Profits" then includes Cost of
  // Goods Sold and any other non-Revenues item placed in the section.
  const grossProfitItems = useMemo(() => placed['gross-profit'] || [], [placed]);
  const grossProfitRevenuesItems = useMemo(
    () => grossProfitItems.filter(it => revenuesOptionId != null && it.parentIds?.includes(revenuesOptionId)),
    [grossProfitItems, revenuesOptionId]
  );
  const grossProfitOtherItems = useMemo(
    () => grossProfitItems.filter(it => !(revenuesOptionId != null && it.parentIds?.includes(revenuesOptionId))),
    [grossProfitItems, revenuesOptionId]
  );
  const netRevenues = grossProfitRevenuesItems.reduce((s, it) => s + (it.amount || 0), 0);
  const totalGrossProfits = sumPlaced('gross-profit');
  const totalOperatingExpenses = sumPlaced('operating-profit');
  const totalOperatingProfit = totalGrossProfits + totalOperatingExpenses;
  const totalOtherIncomeOrExpenses = sumPlaced('non-operating');
  const incomeBeforeTaxes = totalOperatingProfit + totalOtherIncomeOrExpenses;
  const incomeTaxExpenseItem = Object.values(placed).flat().find(it => /income\s*tax/i.test(it.name || ''));
  const incomeTaxExpense = incomeTaxExpenseItem ? incomeTaxExpenseItem.amount : 0;
  const totalNetIncome = incomeBeforeTaxes - Math.abs(incomeTaxExpense);

  // Render helpers ------------------------------------------------------
  const renderItemRow = (it) => (
    <div key={it.id} className="multistep-row placed">
      <span>{it.name}</span>
      <span className={it.amount < 0 ? 'negative' : ''}>{formatMoney(it.amount)}</span>
    </div>
  );

  const renderDropZone = (section) => {
    // Display the placed items by their canonical `order` field (1 → n,
    // top-down) regardless of the order in which the player dropped them.
    const placedItems = [...(placed[section.key] || [])]
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const isDragging = !!dragId;
    const isFlashing = errorFlashSection === section.key;
    const isEmpty = !placedItems.length;

    // Gross Profit splits its rows: Revenues items, then a "Net Revenues"
    // subtotal, then the remaining items (Cost of Goods Sold, …).
    let body;
    if (section.key === 'gross-profit') {
      const revs = [...grossProfitRevenuesItems].sort((a, b) => (a.order || 0) - (b.order || 0));
      const others = [...grossProfitOtherItems].sort((a, b) => (a.order || 0) - (b.order || 0));
      body = (
        <>
          {revs.map(renderItemRow)}
          {revs.length > 0 && (
            <div className="multistep-row subtotal" data-realco-row="net-revenues">
              <span>Net Revenues</span>
              <span className={netRevenues < 0 ? 'negative' : ''}>{formatMoney(netRevenues)}</span>
            </div>
          )}
          {others.map(renderItemRow)}
        </>
      );
    } else if (section.key === 'net-income') {
      // Realco-only: render the dropped item as a "Less: <name>" row
      // with the negative absolute amount, matching standard income-
      // statement formatting for the income tax deduction.
      body = placedItems.map(it => (
        <div key={it.id} className="multistep-row placed">
          <span>Less: {it.name}</span>
          <span className="negative">{formatMoney(-Math.abs(it.amount || 0))}</span>
        </div>
      ));
    } else {
      body = placedItems.map(renderItemRow);
    }

    return (
      <div
        className={[
          'multistep-drop-zone',
          `multistep-drop-zone--${section.key}`,
          isEmpty ? 'empty' : 'filled',
          isDragging ? 'droppable' : '',
          isFlashing ? 'error-flash' : '',
        ].filter(Boolean).join(' ')}
        onDragOver={handleSectionDragOver}
        onDrop={(e) => handleSectionDrop(e, section.key)}
      >
        {isEmpty && (
          <span className="multistep-drop-zone-label">
            {section.label}
            {section.subLabel && <span className="multistep-drop-zone-sublabel"> {section.subLabel}</span>}
          </span>
        )}
        {!isEmpty && body}
      </div>
    );
  };

  const renderBalanceSheet = () => (
    <section
      className="multistep-statement"
      {...((isClassifiedMultistep || isRealco) ? { 'data-tour-id': 'balance-sheet-column' } : {})}
    >
      <div className="multistep-statement-header">
        {isRealco && realcoCompany?.logo && (
          <img
            src={realcoCompany.logo}
            alt={`${realcoCompany.displayName} logo`}
            className="multistep-statement-logo"
          />
        )}
        <h2 className="multistep-statement-title">Balance Sheet</h2>
      </div>

      <div className="multistep-group-header">ASSETS</div>

      <div className="multistep-section-header" data-current-noncurrent="current">Current Assets Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[0])}
      {placed['current-assets']?.length > 0 && (
        <div className="multistep-row subtotal">
          <span>Total Current Assets</span>
          <span>{formatMoney(totalCurrentAssets)}</span>
        </div>
      )}

      <div className="multistep-section-header" data-current-noncurrent="non-current">Non-Current Assets Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[1])}
      {placed['non-current-assets']?.length > 0 && (
        <div className="multistep-row subtotal">
          <span>Total Non-Current Assets</span>
          <span>{formatMoney(totalNonCurrentAssets)}</span>
        </div>
      )}
      {(placed['current-assets']?.length > 0 || placed['non-current-assets']?.length > 0) && (
        <div className="multistep-row total" data-realco-row="total-assets">
          <span>Total Assets</span>
          <span>{formatMoney(totalAssets)}</span>
        </div>
      )}

      <div className="multistep-group-header">LIABILITIES</div>

      <div className="multistep-section-header" data-current-noncurrent="current">Current Liabilities Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[2])}
      {placed['current-liabilities']?.length > 0 && (
        <div className="multistep-row subtotal">
          <span>Total Current Liabilities</span>
          <span>{formatMoney(totalCurrentLiabilities)}</span>
        </div>
      )}

      <div className="multistep-section-header" data-current-noncurrent="non-current">Non-Current Liabilities Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[3])}
      {placed['non-current-liabilities']?.length > 0 && (
        <div className="multistep-row subtotal">
          <span>Total Non-Current Liabilities</span>
          <span>{formatMoney(totalNonCurrentLiabilities)}</span>
        </div>
      )}
      {(placed['current-liabilities']?.length > 0 || placed['non-current-liabilities']?.length > 0) && (
        <div className="multistep-row total" data-realco-row="total-liabilities">
          <span>Total Liabilities</span>
          <span>{formatMoney(totalLiabilities)}</span>
        </div>
      )}

      <div className="multistep-section-header">Equity Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[4])}
      {placed['equity']?.length > 0 && (
        <div className="multistep-row total" data-realco-row="total-equity">
          <span>Total Equity</span>
          <span>{formatMoney(totalEquity)}</span>
        </div>
      )}
    </section>
  );

  const renderIncomeStatement = () => (
    <section
      className="multistep-statement multistep-statement--income"
      {...((isClassifiedMultistep || isRealco) ? { 'data-tour-id': 'income-statement-column' } : {})}
    >
      <div className="multistep-statement-header">
        {isRealco && realcoCompany?.logo && (
          <img
            src={realcoCompany.logo}
            alt={`${realcoCompany.displayName} logo`}
            className="multistep-statement-logo"
          />
        )}
        <h2 className="multistep-statement-title">Income Statement</h2>
      </div>

      <div className="multistep-section-header">Gross Profit Section</div>
      {renderDropZone(INCOME_STATEMENT_SECTIONS[0])}
      {placed['gross-profit']?.length > 0 && (
        <div className="multistep-row total" data-realco-row="total-gross-profits">
          <span>Total Gross Profits</span>
          <span className={totalGrossProfits < 0 ? 'negative' : ''}>{formatMoney(totalGrossProfits)}</span>
        </div>
      )}

      <div className="multistep-section-header" data-operating-nonoperating="operating">Operating Profit Section</div>
      {renderDropZone(INCOME_STATEMENT_SECTIONS[1])}
      {placed['operating-profit']?.length > 0 && (
        <>
          <div className="multistep-row subtotal">
            <span>Total Operating Expenses</span>
            <span className={totalOperatingExpenses < 0 ? 'negative' : ''}>{formatMoney(totalOperatingExpenses)}</span>
          </div>
          <div className="multistep-row total" data-realco-row="total-operating-profit">
            <span>Total Operating Profit</span>
            <span className={totalOperatingProfit < 0 ? 'negative' : ''}>{formatMoney(totalOperatingProfit)}</span>
          </div>
        </>
      )}

      <div className="multistep-section-header" data-operating-nonoperating="non-operating">
        Non-Operating Section <span className="multistep-section-header-sub">(other income/expenses)</span>
      </div>
      {renderDropZone(INCOME_STATEMENT_SECTIONS[2])}
      {placed['non-operating']?.length > 0 && (
        <div className="multistep-row total" data-realco-row="total-other-income-or-expenses">
          <span>Total Other Income or Expenses</span>
          <span className={totalOtherIncomeOrExpenses < 0 ? 'negative' : ''}>{formatMoney(totalOtherIncomeOrExpenses)}</span>
        </div>
      )}

      <div className="multistep-section-header net-income">Net Income</div>
      {/* Realco-only: render Income Before Taxes FIRST, then the drop
          zone (which renders the placed item as "Less: Income Tax
          Expense"), then Total Net Income — matching standard income-
          statement ordering. The drop zone is the Net Income section
          itself, validated via the item's "Net Profit Section" parent. */}
      {isRealco ? (
        <>
          {(placed['operating-profit']?.length > 0 || placed['non-operating']?.length > 0) && (
            <div className="multistep-row">
              <span>Income Before Taxes</span>
              <span className={incomeBeforeTaxes < 0 ? 'negative' : ''}>{formatMoney(incomeBeforeTaxes)}</span>
            </div>
          )}
          {renderDropZone(NET_INCOME_REALCO_SECTION)}
          {(placed['operating-profit']?.length > 0 || placed['non-operating']?.length > 0) && (
            <div className="multistep-row total">
              <span>Total Net Income</span>
              <span className={totalNetIncome < 0 ? 'negative' : ''}>{formatMoney(totalNetIncome)}</span>
            </div>
          )}
        </>
      ) : (
        (placed['operating-profit']?.length > 0 || placed['non-operating']?.length > 0) && (
          <>
            <div className="multistep-row">
              <span>Income Before Taxes</span>
              <span className={incomeBeforeTaxes < 0 ? 'negative' : ''}>{formatMoney(incomeBeforeTaxes)}</span>
            </div>
            <div className="multistep-row">
              <span>Less: Income Tax Expense</span>
              <span className="negative">{formatMoney(-Math.abs(incomeTaxExpense))}</span>
            </div>
            <div className="multistep-row total">
              <span>Total Net Income</span>
              <span className={totalNetIncome < 0 ? 'negative' : ''}>{formatMoney(totalNetIncome)}</span>
            </div>
          </>
        )
      )}
    </section>
  );

  const renderRealcoHeader = () => {
    if (!isRealco || !realcoCompany) return null;
    return (
      <div className="multistep-realco-header">
        <img
          className="multistep-realco-logo"
          src={realcoCompany.logo}
          alt={`${realcoCompany.displayName} logo`}
        />
        <div className="multistep-realco-company">
          <div className="multistep-realco-name">{realcoCompany.displayName}</div>
          <div className="multistep-realco-report">
            <span>10-K Annual Report</span>
            <button
              type="button"
              className="multistep-realco-info"
              aria-label="More info"
            >
              i
            </button>
          </div>
          <div className="multistep-realco-subtitle">(Values reported In millions)</div>
        </div>
      </div>
    );
  };

  const renderStack = () => {
    const stackColumn = (
      <div className="multistep-stack-column">
        {visibleStack.map((item) => {
          const isDraggable = isPlaying;
          // Hover tooltip is shared by classified-multistep and realco;
          // it shows whenever the brick has a description on hand.
          const hasDescription = !!item.description;
          const isHovered = hasDescription && hoveredBrickId === item.id;
          return (
            <div
              key={item.id}
              className={[
                'multistep-brick',
                isDraggable ? 'draggable' : 'locked',
                dragId === item.id ? 'dragging' : '',
                isHovered ? 'hovered' : '',
              ].filter(Boolean).join(' ')}
              draggable={isDraggable}
              onDragStart={(e) => handleDragStart(e, item)}
              onDragEnd={handleDragEnd}
              onMouseEnter={hasDescription ? () => setHoveredBrickId(item.id) : undefined}
              onMouseLeave={hasDescription ? () => setHoveredBrickId(null) : undefined}
              onMouseDown={hasDescription ? () => setHoveredBrickId(null) : undefined}
            >
              <span className="multistep-brick-name">{item.name}</span>
              <span className="multistep-brick-amount">{formatMoney(item.amount)}</span>
              {isHovered && item.description && (
                <Hint content={item.description} side="right" />
              )}
            </div>
          );
        })}
      </div>
    );

    if (!isRealco) return stackColumn;

    return (
      <div className="multistep-center-column">
        {renderRealcoHeader()}
        {stackColumn}
      </div>
    );
  };

  if (!scenarioLevel || !questions.length) return null;

  return (
    <div className="game-level-wrapper multistep-wrapper">
      {showIntro && (
        <Intro
          leftTitle={leftIntroTitle}
          leftSubtitle={leftIntroSubtitle}
          leftDescription={leftIntroDescription}
          rightTitle={rightIntroTitle}
          rightSubtitle={rightIntroSubtitle}
          rightDescription={rightIntroDescription}
          onShowingCompleted={() => setShowIntro(false)}
          isContentCentered={isIntroContentCentered}
          isSubtitleContentReversed={isIntroSubtitleContentReversed}
        />
      )}

      <Header
        time={time}
        totalScore={isLevelScoring ? null : totalScore}
        shouldScorePlaySound={shouldScorePlaySound}
      />

      <section className="multistep-board">
        {renderBalanceSheet()}
        {renderStack()}
        {renderIncomeStatement()}
      </section>

      <footer className="game-footer">
        {isFooterMessageShown && (
          <FooterMessage type={footerMessageType} description={footerMessageDescription} />
        )}
      </footer>

      {modalToShow && (
        <Modal
          content={modalContent}
          isModalOverlayVisible={isModalOverlayVisible}
          isModalSmall={isModalSmall}
        />
      )}
    </div>
  );
}
